import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../utils/axiosInstance";

const initialState = {
  loading: true,
  id: "",
  email: "",
  first_name: "",
  last_name: "",
  phone_number: "",
  date_of_birth: "",
  location: "",
  gender: "",
  token: "",
  promoterRef: "",
  created_at: "",
  role: "",
  genres: "",
  user_info_id: "",
};

/**
 * A thunk action creator that fetches user data from the server.
 * @returns {Promise<any>} - A promise that resolves to the fetched user data.
 */
export const fetchUser = createAsyncThunk("/user/fetchUser", async () => {
  const res = await axiosInstance.get("/api/v1/user/");

  if (res.status !== 200) {
    return initialState;
  }
  return res.data;
});

export const userSlice = createSlice({
  name: "user",
  initialState: initialState,
  reducers: {
    setUser: (state, action) => {
      // Merge: only overwrite fields that are explicitly provided in the payload
      Object.keys(action.payload).forEach((key) => {
        state[key] = action.payload[key];
      });
    },
    changeToken: (state, action) => {
      state.token = action.payload;
      // A token being applied means auth boot has settled — release the loading
      // gate. Without this, a user who has a saved token but no auth session
      // (e.g. a dead/expired login) is left with loading=true forever, and
      // AuthorizedLayout renders its full-screen spinner indefinitely.
      state.loading = false;

      localStorage.setItem("token", action.payload);
    },
    setPromoterRef: (state, action) => {
      state.promoterRef = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        const user = action.payload.user;

        state.loading = user.loading;
        state.id = user.id;
        state.email = user.email;
        state.first_name = user.first_name;
        state.last_name = user.last_name;
        state.phone_number = user.phone_number;
        state.date_of_birth = user.date_of_birth;
        state.location = user.location;
        state.gender = user.gender;
        state.token = user.token;
        state.created_at = user.created_at;
        state.genres = user.genres;
        state.user_info_id = user.user_info_id;
        state.loading = false;
      })
      .addCase(fetchUser.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setUser, changeToken, setPromoterRef } = userSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.user.value)`
export const selectUser = (state) => state.user;

export default userSlice.reducer;
